import { api } from '@/common/http/axios';
import type { SystemConfigSource } from './simulation';

/** 中台模型同步 API。后端：simulation_platform/api/routers/model_sync.py（字段驼峰）。 */

export interface Reason {
  code: string;
  message: string;
  detail?: unknown;
  /** 只拦这个模型类别（其余照常同步）；空 = 拦整个发版 */
  model?: string | null;
}

export interface PreviewModel {
  name: string;
  /** 本次登记的版本；跳过的模型类别为 null */
  version: string | null;
  install_dir: string;
  /** 本次以自动名新登记的模型类别 */
  auto_named: boolean;
  category: string | null;
  type: string | null;
  /** 二级分类中文名（与中台页面上的模型类型名一致） */
  type_label?: string | null;
  /** 中台记录；type_label 为该记录中台类型的中文名 */
  records: { id: string; name: string; version?: string; type?: string; type_label?: string | null }[];
  upload: string[];
  reuse: string[];
  /** 基版里该模型的版本；null = 基版没有（新增） */
  base_version: string | null;
  /** 与基版版本相同，本次不改 */
  unchanged: boolean;
  dropped_tags: string[];
  note: string;
  /** 本次不同步（新主版本里沿用基版版本）；老快照没有该字段 */
  skipped?: boolean;
  /** 不可同步（预览检查未通过），不能勾选同步 */
  blocked?: boolean;
  /** 不可同步的原因 */
  skip_reasons?: string[];
}

/** 预览 / 子项快照里的变化摘要（后端 analysis.Analysis.to_summary，字段蛇形）。 */
export interface SyncSummary {
  ndp_version: string;
  ndp_release_name: string | null;
  kind: string | null;
  major_group: string | null;
  /** 基版主版本（基版 + 中台改动 = 新主版本） */
  base_release: string | null;
  /** 同大版本下已有清单完全相同的主版本（无需同步时给出） */
  identical_release: string | null;
  new_release_name: string | null;
  change_summary: string | null;
  /** 跳过说明：跳过：traffic_light（空壳 onnx，沿用基版 xxx） */
  skip_summary?: string | null;
  /** 不可同步、固定跳过的模型类别 */
  blocked_models?: string[];
  /** 用户确认时主动跳过的模型类别（子项快照里） */
  skip_models?: string[];
  models: PreviewModel[];
  /** 本次将以自动名新登记的模型类别 */
  new_names: string[];
  /** 基版里来自中台、本次中台发版没有的模型（默认保留，可勾选移除） */
  removable: { name: string; version: string | null }[];
  /** 已确定从基版移除的模型类别 */
  removals: string[];
  /** 开启 engine 同步的模型类别 */
  engine_models?: string[];
  skipped_records: { record_id: string; name: string; reason: string }[];
  skipped_files: number;
  skipped_dirs: { install_dir: string; reason: string; records: string[] }[];
  files_to_transfer: number;
  bytes_to_transfer: number;
}

/** 主版本清单里的一条改动（model_service manifest.changes）。 */
export interface ReleaseChangeRow {
  op: string;
  name: string;
  from?: string | null;
  to?: string | null;
}

export interface PreviewItem {
  ndpVersion: string;
  /** syncable 全部可同步 / partial 部分模型类别不可同步 / blocked / already_synced */
  verdict: 'syncable' | 'partial' | 'blocked' | 'already_synced';
  ndpVersionName: string | null;
  releaseKind: string | null;
  reasons: Reason[];
  warnings: string[];
  summary: SyncSummary | null;
}

export interface PreviewResponse {
  previewId: string;
  expiresIn: number;
  items: PreviewItem[];
}

export interface SyncItem {
  id: number;
  taskId: number;
  ndpVersion: string;
  ndpVersionName: string | null;
  releaseKind: string | null;
  status: string;
  stage: string | null;
  progress: { done_files?: number; total_files?: number } | null;
  releaseName: string | null;
  result: (SyncSummary & {
    release?: string;
    changes?: ReleaseChangeRow[];
    warnings?: string[];
  }) | null;
  previewSnapshot: PreviewItem | null;
  /** 该子项使用的基版主版本 */
  baseRelease: string | null;
  /** 用户选择从基版移除的模型类别 */
  removeModels: string[] | null;
  /** 本次不同步的模型类别（不可同步的 + 用户勾掉的） */
  skipModels?: string[] | null;
  /** 要生成的主版本号 */
  targetReleaseName?: string | null;
  failCode: string | null;
  failReason: string | null;
  failDetail: { items?: unknown } & Record<string, unknown> | null;
  startTime: string | null;
  finishTime: string | null;
  updateTime: string | null;
}

export interface SyncTask {
  id: number;
  name: string;
  status: string;
  totalItems: number;
  succeededItems: number;
  alreadySyncedItems: number;
  failedItems: number;
  cancelledItems: number;
  failReason: string | null;
  creator: string;
  retryOf: number | null;
  /** 开启 engine 同步的模型类别 */
  engineModels?: string[];
  createTime: string;
  submitTime: string | null;
  finishTime: string | null;
  updateTime: string | null;
  ndpVersions: string[];
}

export interface SyncTaskDetail extends SyncTask {
  items: SyncItem[];
  /** 旧版「同步后 current」汇总；新流程不再产出，仅为兼容保留 */
}

export interface PageMeta {
  current: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface TaskListQuery {
  ndpVersion?: string;
  status?: string[];
  creator?: string;
  releaseName?: string;
  createTimeFrom?: string;
  createTimeTo?: string;
  current?: number;
  pageSize?: number;
}

export interface NdpRelease {
  ndpVersion: string;
  name: string | null;
  releaseKind: string | null;
  modelCount: number;
  descInfo: string | null;
  createTime: string | null;
  syncedRelease: string | null;
}

/** base 为空 = 后端默认最新正式主版本。 */
/** engineModels：开启 engine 同步的模型类别（只同步 GPU engine，DLA 跳过）；空 = 不同步 engine。 */
export async function previewSync(
  ndpVersions: string[], base?: string | null, engineModels: string[] = [], noBase = false,
): Promise<PreviewResponse> {
  // noBase：不使用基版，新主版本只包含中台本次同步的模型（不能与 base 同时传）
  const res = await api.post('/model/sync-tasks:preview', { ndpVersions, base: noBase ? null : base || null, engineModels, noBase },
    { timeout: 180_000 } as any);
  return res.data.data;
}

export async function createSyncTask(params: {
  previewId: string; ndpVersions: string[]; name?: string; retryOf?: number;
  /** {发版号: [从基版移除的模型类别]} */
  removals?: Record<string, string[]>;
  /** {发版号: 主版本号}；不传的用预览时自动生成的名字 */
  releaseNames?: Record<string, string>;
  /** {发版号: [本次不同步的模型类别]}；只能选预览里可同步的，不可同步的后端总是跳过 */
  skipModels?: Record<string, string[]>;
}): Promise<SyncTaskDetail> {
  const res = await api.post('/model/sync-tasks', params);
  return res.data.data;
}

export async function listSyncTasks(q: TaskListQuery): Promise<{ items: SyncTask[]; page: PageMeta }> {
  const res = await api.get('/model/sync-tasks', {
    params: q,
    // FastAPI 的多值查询参数要求 status=a&status=b（axios 默认是 status[]=a）
    paramsSerializer: { indexes: null },
  });
  return { items: res.data.data || [], page: res.data.page as PageMeta };
}

export async function getSyncTask(id: number): Promise<SyncTaskDetail> {
  const res = await api.get(`/model/sync-tasks/${id}`);
  return res.data.data;
}

export async function cancelSyncTask(id: number): Promise<SyncTaskDetail> {
  const res = await api.post(`/model/sync-tasks/${id}:cancel`);
  return res.data.data;
}

/** 子项下单个文件的 BOS 抓取状态。 */
export interface SyncFile {
  id: number;
  fileName: string;
  tag: string | null;
  modelName: string | null;
  md5: string;
  size: number | null;
  /** pending / fetching / fetched / verified / failed */
  status: string;
  attempts: number;
  fetchJobId: string | null;
  fileSha256: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  submitTime: string | null;
  finishTime: string | null;
  updateTime: string | null;
}

export async function getSyncItemFiles(itemId: number): Promise<SyncFile[]> {
  const res = await api.get(`/model/sync-items/${itemId}/files`);
  return res.data.data || [];
}

/** 发版联想一次最多返回的条数（后端按版本号倒序截取） */
export const NDP_RELEASE_LIMIT = 30;

export async function listNdpReleases(keyword?: string): Promise<NdpRelease[]> {
  // 联想失败不打断输入，由组件静默处理
  const res = await api.get('/model/sync-tasks/ndp-releases', { params: { keyword, limit: NDP_RELEASE_LIMIT }, _skipErrorHandler: true } as any);
  return res.data.data || [];
}

/* ------------------------------------------------------------------ *
 * 系统配置：中台同步忽略目录 / 模型分类
 * ------------------------------------------------------------------ */

export interface JsonConfig {
  currentValue: string;
  defaultValue: string;
  source: SystemConfigSource;
}
export type NdpModelSlotsConfig = JsonConfig;

/** 值形如 {"ignored": {目录: {reason}}}。 */
export async function getNdpModelSlots(): Promise<JsonConfig> {
  const res = await api.get('/system-config/ndp-model-slots');
  return res.data.data;
}

export async function updateNdpModelSlots(slots: string): Promise<JsonConfig> {
  const res = await api.put('/system-config/ndp-model-slots', { slots }, { _skipErrorHandler: true } as any);
  return res.data.data;
}

export async function resetNdpModelSlots(): Promise<JsonConfig> {
  const res = await api.delete('/system-config/ndp-model-slots');
  return res.data.data;
}

/** 值形如 {一级分类: {label, types: [二级分类]}}。 */
export async function getModelCategories(): Promise<JsonConfig> {
  const res = await api.get('/system-config/model-categories');
  return res.data.data;
}

/** 错误由调用方就地展示（如「二级分类仍有模型在用」），不走全局弹窗。 */
export async function updateModelCategories(categories: string): Promise<JsonConfig> {
  const res = await api.put('/system-config/model-categories', { categories }, { _skipErrorHandler: true } as any);
  return res.data.data;
}

/** 允许上传的模型类别：值为 JSON 数组。 */
export async function getModelUploadNamesConfig(): Promise<JsonConfig> {
  const res = await api.get('/system-config/model-upload-names');
  return res.data.data;
}

/** 错误由调用方就地展示，不走全局弹窗。 */
export async function updateModelUploadNames(names: string): Promise<JsonConfig> {
  const res = await api.put('/system-config/model-upload-names', { names }, { _skipErrorHandler: true } as any);
  return res.data.data;
}

export async function resetModelUploadNames(): Promise<JsonConfig> {
  const res = await api.delete('/system-config/model-upload-names');
  return res.data.data;
}

export async function resetModelCategories(): Promise<JsonConfig> {
  const res = await api.delete('/system-config/model-categories');
  return res.data.data;
}
